function numberValue(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function text(value) {
  return String(value ?? '').trim();
}

function usageRows(attacks = [], limit = 10) {
  const source = Array.isArray(attacks) ? attacks : [];
  const usage = new Map();

  for (const attack of source) {
    const seen = new Set();
    const deck = Array.isArray(attack?.deck) ? attack.deck : [];
    for (const item of deck) {
      const code = text(item?.code ?? item?.nikkeId ?? item?.nikke_id ?? item?.id);
      if (!code || seen.has(code)) continue;
      seen.add(code);
      const current = usage.get(code) || {
        code,
        name: text(item?.name ?? item?.officialName ?? code) || code,
        imageUrl: text(item?.imageUrl ?? item?.image_url),
        count: 0,
        memberKeys: new Set(),
        bossNos: new Set(),
      };
      current.count += 1;
      if (attack?.memberKey) current.memberKeys.add(String(attack.memberKey));
      if (Number(attack?.bossNo) > 0) current.bossNos.add(Number(attack.bossNo));
      if (!current.imageUrl && item?.imageUrl) current.imageUrl = text(item.imageUrl);
      if ((!current.name || current.name === current.code) && item?.name) current.name = text(item.name);
      usage.set(code, current);
    }
  }

  return [...usage.values()]
    .map((item) => ({
      code: item.code,
      name: item.name,
      imageUrl: item.imageUrl,
      count: item.count,
      usagePercent: source.length > 0 ? (item.count / source.length) * 100 : 0,
      memberCount: item.memberKeys.size,
      bossCount: item.bossNos.size,
      bossNos: [...item.bossNos].sort((a, b) => a - b),
    }))
    .sort((a, b) => b.count - a.count || b.memberCount - a.memberCount || b.bossCount - a.bossCount || a.name.localeCompare(b.name, 'ko-KR'))
    .slice(0, Math.max(1, numberValue(limit, 10)));
}

function memberRows(ranking = []) {
  return (Array.isArray(ranking) ? ranking : [])
    .filter((row) => numberValue(row?.attackCount) > 0)
    .map((row) => {
      const attacks = Array.isArray(row.attacks) ? row.attacks : [];
      const bossNos = new Set(attacks.map((attack) => numberValue(attack?.bossNo)).filter(Boolean));
      const topAttack = attacks.reduce((best, attack) => (
        numberValue(attack?.damageEok) > numberValue(best?.damageEok) ? attack : best
      ), null);
      return {
        memberKey: String(row.memberKey || ''),
        nickname: String(row.nickname || '닉네임 확인 불가'),
        rank: numberValue(row.rank),
        attackCount: numberValue(row.attackCount),
        totalDamageEok: numberValue(row.totalDamageEok),
        weightedDamageEok: numberValue(row.weightedDamageEok),
        contributionPercent: numberValue(row.contributionPercent),
        rawContributionPercent: numberValue(row.rawContributionPercent),
        killCount: numberValue(row.killCount),
        bossCount: bossNos.size,
        averageDamageEok: attacks.length ? numberValue(row.totalDamageEok) / attacks.length : 0,
        topDamageEok: numberValue(topAttack?.damageEok),
        topBossNo: numberValue(topAttack?.bossNo) || null,
      };
    })
    .sort((a, b) => a.rank - b.rank || b.weightedDamageEok - a.weightedDamageEok)
    .slice(0, 6);
}

export function buildRaidSettlementReport(settlement = {}) {
  const ranking = Array.isArray(settlement?.ranking) ? settlement.ranking : [];
  const bossTotals = Array.isArray(settlement?.bossTotals) ? settlement.bossTotals : [];
  const allAttacks = bossTotals.flatMap((boss) => Array.isArray(boss?.attacks) ? boss.attacks : []);
  const memberCount = ranking.length;
  const attackCount = numberValue(settlement?.attackCount);
  const participantCount = numberValue(settlement?.participantCount);
  const completedMemberCount = numberValue(settlement?.completedMemberCount);
  const attackCapacity = memberCount * 3;
  const killCount = bossTotals.reduce((sum, boss) => sum + numberValue(boss?.killCount), 0);
  const topContributor = ranking.find((row) => numberValue(row?.attackCount) > 0) || null;
  const topAttack = allAttacks.reduce((best, attack) => (
    numberValue(attack?.damageEok) > numberValue(best?.damageEok) ? attack : best
  ), null);

  return {
    memberCount,
    attackCount,
    attackCapacity,
    participantCount,
    completedMemberCount,
    killCount,
    clearedBossCount: bossTotals.filter((boss) => Boolean(boss?.isCleared)).length,
    participationPercent: memberCount > 0 ? (participantCount / memberCount) * 100 : 0,
    completionPercent: memberCount > 0 ? (completedMemberCount / memberCount) * 100 : 0,
    attackUsePercent: attackCapacity > 0 ? (attackCount / attackCapacity) * 100 : 0,
    averageDamageEok: numberValue(settlement?.averageDamageEok),
    totalDamageEok: numberValue(settlement?.totalDamageEok),
    topContributor: topContributor ? {
      memberKey: String(topContributor.memberKey || ''),
      nickname: String(topContributor.nickname || '닉네임 확인 불가'),
      contributionPercent: numberValue(topContributor.contributionPercent),
      totalDamageEok: numberValue(topContributor.totalDamageEok),
    } : null,
    topAttack: topAttack ? {
      nickname: String(topAttack.nickname || '닉네임 확인 불가'),
      damageEok: numberValue(topAttack.damageEok),
      bossNo: numberValue(topAttack.bossNo) || null,
      bossName: String(topAttack.bossName || ''),
    } : null,
    memberHighlights: memberRows(ranking),
    nikkeUsage: usageRows(allAttacks, 10),
    bossNikkeUsage: bossTotals.map((boss) => ({
      bossNo: numberValue(boss?.bossNo),
      bossName: String(boss?.bossName || `보스 ${numberValue(boss?.bossNo)}`),
      rows: usageRows(boss?.attacks, 3),
    })),
  };
}
